import axios from 'axios';
import { getAuthHeader } from './authService';

const API_URL = `${process.env.REACT_APP_SERVER_URL}/api/magazine`;

const headers = () => getAuthHeader();

export const getPublications = async () => {
  try {
    const response = await axios.get(`${API_URL}/publications`, { headers: headers() });
    return response.data;
  } catch (error: any) {
    console.log(error.message);
    return [];
  }
};

export const createPublication = async (data: any) => {
  const response = await axios.post(`${API_URL}/publications`, data, { headers: headers() });
  return response.data;
};

export const updatePublication = async (id: number, data: any) => {
  const response = await axios.put(`${API_URL}/publications/${id}`, data, { headers: headers() });
  return response.data;
};

export const deletePublication = async (id: number) => {
  const response = await axios.delete(`${API_URL}/publications/${id}`, { headers: headers() });
  return response.data;
};

export const getPublicationLogoUrl = (logoPath?: string | null) => {
  if (!logoPath) return '';
  if (/^https?:\/\//i.test(logoPath)) return logoPath;
  const serverUrl = (process.env.REACT_APP_SERVER_URL || '').replace(/\/$/, '');
  return `${serverUrl}/api/uploads/${logoPath.replace(/^\//, '')}`;
};

export const getIssues = async (publicationId?: string) => {
  try {
    const params = publicationId ? { publication_id: publicationId } : {};
    const response = await axios.get(`${API_URL}/issues`, { headers: headers(), params });
    return response.data;
  } catch (error: any) {
    console.log(error.message);
    return [];
  }
};

export const createIssue = async (data: any) => {
  const response = await axios.post(`${API_URL}/issues`, data, { headers: headers() });
  return response.data;
};

export type DuplicateIssuePayload = {
  publication_id: number;
  title: string;
  issue_date?: string | null;
  status: string;
  copy_inventory: boolean;
};

export const duplicateIssue = async (id: number, data: DuplicateIssuePayload) => {
  const response = await axios.post(`${API_URL}/issues/${id}/duplicate`, data, { headers: headers() });
  return response.data as { issue: any; inventory_copied: number };
};

export const updateIssue = async (id: number, data: any) => {
  const response = await axios.put(`${API_URL}/issues/${id}`, data, { headers: headers() });
  return response.data;
};

export const deleteIssue = async (id: number) => {
  const response = await axios.delete(`${API_URL}/issues/${id}`, { headers: headers() });
  return response.data;
};

export const getSlots = async (issueId?: string) => {
  try {
    const params = issueId ? { issue_id: issueId } : {};
    const response = await axios.get(`${API_URL}/slots`, { headers: headers(), params });
    return response.data;
  } catch (error: any) {
    console.log(error.message);
    return [];
  }
};

export const createSlot = async (data: any) => {
  const response = await axios.post(`${API_URL}/slots`, data, { headers: headers() });
  return response.data;
};

export const updateSlot = async (id: number, data: any) => {
  const response = await axios.put(`${API_URL}/slots/${id}`, data, { headers: headers() });
  return response.data;
};

export const deleteSlot = async (id: number) => {
  const response = await axios.delete(`${API_URL}/slots/${id}`, { headers: headers() });
  return response.data;
};

export type IssueBookingReport = {
  issue: {
    id: number;
    title: string;
    issue_date: string | null;
    status: string;
    publication: { id: number; name: string } | null;
  };
  slots: Array<{
    id: number;
    label: string;
    size: string | null;
    price: string | number;
    quantity: number;
    booked_count: number;
    available: number;
    status: string;
  }>;
  bookings: Array<{
    source: string;
    proposal_id: number | null;
    proposal_status: string | null;
    proposal_currency?: string | null;
    proposal_gbp_rate: number;
    company: { id: number; name: string } | null;
    sales_rep: { id: number; username: string; email: string } | null;
    ad_slot: {
      id: number;
      label: string;
      size: string | null;
      rate_card_price: string | number;
    };
    booked_price: number;
    href: string | null;
  }>;
  summary: {
    slot_count: number;
    booking_count: number;
    total_booked_revenue: number;
  };
};

export const getIssueBookingReport = async (
  issueId: string | number,
  status?: string
): Promise<IssueBookingReport | null> => {
  try {
    const params = status ? { status } : {};
    const response = await axios.get(`${API_URL}/issues/${issueId}/report`, {
      headers: headers(),
      params,
    });
    return response.data;
  } catch (error: any) {
    console.log(error.message);
    return null;
  }
};

export const getBookings = async () => {
  try {
    const response = await axios.get(`${API_URL}/bookings`, { headers: headers() });
    return response.data;
  } catch (error: any) {
    console.log(error.message);
    return [];
  }
};

export const createBooking = async (data: any) => {
  const response = await axios.post(`${API_URL}/bookings`, data, { headers: headers() });
  return response.data;
};

export const updateBooking = async (id: number, data: any) => {
  const response = await axios.put(`${API_URL}/bookings/${id}`, data, { headers: headers() });
  return response.data;
};

export const deleteBooking = async (id: number) => {
  const response = await axios.delete(`${API_URL}/bookings/${id}`, { headers: headers() });
  return response.data;
};
